import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
}

export default function Skeleton({
  className = '',
  width,
  height,
  rounded = 'lg',
}: SkeletonProps) {
  return (
    <div
      className={`animate-pulse ${className}`}
      style={{
        width,
        height,
        borderRadius:
          rounded === 'full' ? '9999px'
          : rounded === '2xl' ? '16px'
          : rounded === 'xl' ? '12px'
          : rounded === 'lg' ? '8px'
          : rounded === 'md' ? '6px'
          : '4px',
        background: 'linear-gradient(90deg, #ede9fe 25%, #f5f3ff 50%, #ede9fe 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s ease-in-out infinite',
      }}
    />
  );
}

export function SkeletonCard() {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: '#ffffff', border: '1px solid #e9e5f5' }}
    >
      <div className="flex items-start justify-between mb-3">
        <Skeleton width={40} height={40} rounded="full" />
        <Skeleton width={60} height={20} rounded="full" />
      </div>
      <Skeleton width="60%" height={28} className="mb-2" />
      <Skeleton width="40%" height={14} />
    </div>
  );
}

export function SkeletonChart() {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: '#ffffff', border: '1px solid #e9e5f5' }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <Skeleton width={140} height={18} className="mb-2" />
          <Skeleton width={100} height={12} />
        </div>
        <Skeleton width={90} height={32} rounded="xl" />
      </div>
      <div className="flex items-end gap-2 h-[200px] pt-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col gap-1 justify-end">
            <Skeleton height={`${30 + Math.random() * 60}%`} className="w-full" rounded="sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: '#ffffff', border: '1px solid #e9e5f5' }}
    >
      <div className="flex items-center justify-between mb-4">
        <Skeleton width={160} height={18} />
        <Skeleton width={180} height={32} rounded="xl" />
      </div>
      <div className="flex gap-3 mb-4">
        <Skeleton width={120} height={32} rounded="xl" />
        <Skeleton width={120} height={32} rounded="xl" />
        <Skeleton width={120} height={32} rounded="xl" />
      </div>
      <div className="space-y-0">
        {/* Header row */}
        <div className="flex gap-4 py-3" style={{ borderBottom: '1px solid #e9e5f5' }}>
          <Skeleton width="15%" height={12} />
          <Skeleton width="15%" height={12} />
          <Skeleton width="30%" height={12} />
          <Skeleton width="20%" height={12} />
          <Skeleton width="15%" height={12} />
        </div>
        {/* Data rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-3" style={{ borderBottom: '1px solid #e9e5f5' }}>
            <Skeleton width="15%" height={14} />
            <Skeleton width="15%" height={22} rounded="full" />
            <Skeleton width="30%" height={14} />
            <Skeleton width="20%" height={14} />
            <Skeleton width="15%" height={14} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonDonut() {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: '#ffffff', border: '1px solid #e9e5f5' }}
    >
      <div className="flex items-center justify-between mb-4">
        <Skeleton width={100} height={18} />
        <Skeleton width={70} height={28} rounded="xl" />
      </div>
      <div className="flex items-center justify-center py-6">
        <Skeleton width={160} height={160} rounded="full" />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton width={8} height={8} rounded="full" />
            <Skeleton width={60} height={12} />
          </div>
        ))}
      </div>
    </div>
  );
}
